import i18n from "i18next";

import {
  AgriculturePolicyDetails,
  HazardIdEnum,
  LocationRiskAssessment,
  PolicyDetails,
} from "../hazardIdEnum";
import { getChanceOfHappeningInPercentage } from "../utils";
import { PermissionIdEnum } from "../../util/types/permissions/permissionIdEnum";

type PartialHazard = Required<Pick<Hazard, "name" | "colorRange" | "description" | "label" | "pages">>;

const getInlandFloodHazardConfigByYear = (year: number, country: string): PartialHazard => ({
  name: `Inland Flood ${year} years`,
  colorRange: ["#f9f9f9", "#2b83ba", "#abdda4", "#F9E03F", "#fdae61", "#d7191c"],
  description: i18n.t("riskExposureMapExplained.inlandFloodIta", {
    noOfYears: year,
    chanceOfHappening: getChanceOfHappeningInPercentage(year),
    copyRight: "2025",
    country:
      country === "ITA" ? i18n.t("riskAssessment.countryItaly") : i18n.t("riskAssessment.countryEurope"),
  }),
  label: `${i18n.t("riskAssessment.inlandFlood")} (${country}) - ${year}yr`,
  pages: [PolicyDetails, LocationRiskAssessment],
});

const getInlandFloodHazardGlobalConfigByYear = (year: number): PartialHazard => ({
  name: `Inland Flood ${year} years`,
  colorRange: ["#f9f9f9", "#2b83ba", "#abdda4", "#F9E03F", "#fdae61", "#d7191c"],
  description: i18n.t("riskExposureMapExplained.inlandFloodIta", {
    noOfYears: year,
    chanceOfHappening: getChanceOfHappeningInPercentage(year),
    copyRight: "1995-2021",
    country: i18n.t("riskAssessment.countryGlobal"),
  }),
  label: `${i18n.t("riskAssessment.inlandFlood")} (Global) - ${year}yr`,
  pages: [PolicyDetails, LocationRiskAssessment],
});

const getCoastalFloodHazardConfigByYear = (year: number): PartialHazard => ({
  name: `Coastal Flood ${year} years`,
  colorRange: ["#f9f9f9", "#2b83ba", "#abdda4", "#F9E03F", "#fdae61", "#d7191c"],
  description: i18n.t("riskExposureMapExplained.coastalFloodIta", {
    noOfYears: year,
    chanceOfHappening: getChanceOfHappeningInPercentage(year),
  }),
  label: `${i18n.t("riskAssessment.coastalFlood")} (EUR) - ${year}yr`,
  pages: [PolicyDetails, LocationRiskAssessment],
});

const getFlashFloodHazardConfigByYear = (hour: number): PartialHazard => ({
  name: `Flash Flood ${hour}h`,
  colorRange: ["#f9f9f9", "#eaf8ff", "#d0efff", "#187bcd", "#03254c"],
  description: i18n.t("riskExposureMapExplained.flashFloodIta", { hour }),
  label: `${i18n.t("riskAssessment.flashflood")} (ITA) - ${hour}h`,
  pages: [PolicyDetails, AgriculturePolicyDetails, LocationRiskAssessment],
});

export const getFloodHazardsList = (): Hazard[] => [
  // Inland 2025 - Europe
  {
    cysId: HazardIdEnum.InlandFloodEur10y,
    // permissions: ["CYS_RISK_INLANDFLOOD_WHOLE_EUR_EFAS_C_RP0010"],
    permissions: [PermissionIdEnum.InlandFloodEur10y],
    ...getInlandFloodHazardConfigByYear(10, "EUR"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodEur20y,
    // permissions: ["CYS_RISK_INLANDFLOOD_WHOLE_EUR_EFAS_C_RP0020"],
    permissions: [PermissionIdEnum.InlandFloodEur20y],
    ...getInlandFloodHazardConfigByYear(20, "EUR"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodEur30y,
    // permissions: ["CYS_RISK_INLANDFLOOD_WHOLE_EUR_EFAS_C_RP0030"],
    permissions: [PermissionIdEnum.InlandFloodEur30y],
    ...getInlandFloodHazardConfigByYear(30, "EUR"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodEur40y,
    // permissions: ["CYS_RISK_INLANDFLOOD_WHOLE_EUR_EFAS_C_RP0040"],
    permissions: [PermissionIdEnum.InlandFloodEur40y],
    ...getInlandFloodHazardConfigByYear(40, "EUR"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodEur50y,
    // permissions: ["CYS_RISK_INLANDFLOOD_WHOLE_EUR_EFAS_C_RP0050"],
    permissions: [PermissionIdEnum.InlandFloodEur50y],
    ...getInlandFloodHazardConfigByYear(50, "EUR"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodEur75y,
    // permissions: ["CYS_RISK_INLANDFLOOD_WHOLE_EUR_EFAS_C_RP0075"],
    permissions: [PermissionIdEnum.InlandFloodEur75y],
    ...getInlandFloodHazardConfigByYear(75, "EUR"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodEur100y,
    // permissions: ["CYS_RISK_INLANDFLOOD_WHOLE_EUR_EFAS_C_RP0100"],
    permissions: [PermissionIdEnum.InlandFloodEur100y],
    ...getInlandFloodHazardConfigByYear(100, "EUR"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodEur200y,
    // permissions: ["CYS_RISK_INLANDFLOOD_WHOLE_EUR_EFAS_C_RP0200"],
    permissions: [PermissionIdEnum.InlandFloodEur200y],
    ...getInlandFloodHazardConfigByYear(200, "EUR"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodEur500y,
    // permissions: ["CYS_RISK_INLANDFLOOD_WHOLE_EUR_EFAS_C_RP0500"],
    permissions: [PermissionIdEnum.InlandFloodEur500y],
    ...getInlandFloodHazardConfigByYear(500, "EUR"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },

  // Inland 2025 - Italy

  {
    cysId: HazardIdEnum.InlandFloodIta10y,
    // permissions: ["CYS_RISK_INLANDFLOOD_EUR_EFAS_C_RP0010"],
    permissions: [PermissionIdEnum.InlandFloodIta10y],
    ...getInlandFloodHazardConfigByYear(10, "ITA"),
    pages: [PolicyDetails, AgriculturePolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodIta20y,
    // permissions: ["CYS_RISK_INLANDFLOOD_EUR_EFAS_C_RP0020"],
    permissions: [PermissionIdEnum.InlandFloodIta20y],
    ...getInlandFloodHazardConfigByYear(20, "ITA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodIta30y,
    // permissions: ["CYS_RISK_INLANDFLOOD_EUR_EFAS_C_RP0030"],
    permissions: [PermissionIdEnum.InlandFloodIta30y],
    ...getInlandFloodHazardConfigByYear(30, "ITA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodIta40y,
    // permissions: ["CYS_RISK_INLANDFLOOD_EUR_EFAS_C_RP0040"],
    permissions: [PermissionIdEnum.InlandFloodIta40y],
    ...getInlandFloodHazardConfigByYear(40, "ITA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodIta50y,
    // permissions: ["CYS_RISK_INLANDFLOOD_EUR_EFAS_C_RP0050"],
    permissions: [PermissionIdEnum.InlandFloodIta50y],
    ...getInlandFloodHazardConfigByYear(50, "ITA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodIta75y,
    // permissions: ["CYS_RISK_INLANDFLOOD_EUR_EFAS_C_RP0075"],
    permissions: [PermissionIdEnum.InlandFloodIta75y],
    ...getInlandFloodHazardConfigByYear(75, "ITA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodIta100y,
    // permissions: ["CYS_RISK_INLANDFLOOD_EUR_EFAS_C_RP0100"],
    permissions: [PermissionIdEnum.InlandFloodIta100y],
    ...getInlandFloodHazardConfigByYear(100, "ITA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodIta200y,
    // permissions: ["CYS_RISK_INLANDFLOOD_EUR_EFAS_C_RP0200"],
    permissions: [PermissionIdEnum.InlandFloodIta200y],
    ...getInlandFloodHazardConfigByYear(200, "ITA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodIta500y,
    // permissions: ["CYS_RISK_INLANDFLOOD_EUR_EFAS_C_RP0500"],
    permissions: [PermissionIdEnum.InlandFloodIta500y],
    ...getInlandFloodHazardConfigByYear(500, "ITA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },

  // USA

  {
    cysId: HazardIdEnum.InlandFloodUsa10y,
    // permissions: ["CYS_RISK_INLANDFLOOD_USA_EFAS_C_RP0010"],
    permissions: [PermissionIdEnum.InlandFloodUsa10y],
    ...getInlandFloodHazardConfigByYear(10, "USA"),
    pages: [PolicyDetails, AgriculturePolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodUsa20y,
    // permissions: ["CYS_RISK_INLANDFLOOD_USA_EFAS_C_RP0020"],
    permissions: [PermissionIdEnum.InlandFloodUsa20y],
    ...getInlandFloodHazardConfigByYear(20, "USA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },

  {
    cysId: HazardIdEnum.InlandFloodUsa50y,
    // permissions: ["CYS_RISK_INLANDFLOOD_USA_EFAS_C_RP0050"],
    permissions: [PermissionIdEnum.InlandFloodUsa50y],
    ...getInlandFloodHazardConfigByYear(50, "USA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodUsa75y,
    // permissions: ["CYS_RISK_INLANDFLOOD_USA_EFAS_C_RP0075"],
    permissions: [PermissionIdEnum.InlandFloodUsa75y],
    ...getInlandFloodHazardConfigByYear(75, "USA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodUsa100y,
    // permissions: ["CYS_RISK_INLANDFLOOD_USA_EFAS_C_RP0100"],
    permissions: [PermissionIdEnum.InlandFloodUsa100y],
    ...getInlandFloodHazardConfigByYear(100, "USA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodUsa200y,
    // permissions: ["CYS_RISK_INLANDFLOOD_USA_EFAS_C_RP0200"],
    permissions: [PermissionIdEnum.InlandFloodUsa200y],
    ...getInlandFloodHazardConfigByYear(200, "USA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodUsa500y,
    // permissions: ["CYS_RISK_INLANDFLOOD_USA_EFAS_C_RP0500"],
    permissions: [PermissionIdEnum.InlandFloodUsa500y],
    ...getInlandFloodHazardConfigByYear(500, "USA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },

  // BRAZIL

  {
    cysId: HazardIdEnum.InlandFloodBra10y,
    // permissions: ["CYS_RISK_INLANDFLOOD_BRA_EFAS_C_RP0010"],
    permissions: [PermissionIdEnum.InlandFloodBra10y],
    ...getInlandFloodHazardConfigByYear(10, "BRA"),
    pages: [PolicyDetails, AgriculturePolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodBra20y,
    // permissions: ["CYS_RISK_INLANDFLOOD_BRA_EFAS_C_RP0020"],
    permissions: [PermissionIdEnum.InlandFloodBra20y],
    ...getInlandFloodHazardConfigByYear(20, "BRA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },

  {
    cysId: HazardIdEnum.InlandFloodBra50y,
    // permissions: ["CYS_RISK_INLANDFLOOD_BRA_EFAS_C_RP0050"],
    permissions: [PermissionIdEnum.InlandFloodBra50y],
    ...getInlandFloodHazardConfigByYear(50, "BRA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodBra75y,
    // permissions: ["CYS_RISK_INLANDFLOOD_BRA_EFAS_C_RP0075"],
    permissions: [PermissionIdEnum.InlandFloodBra75y],
    ...getInlandFloodHazardConfigByYear(75, "BRA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodBra100y,
    // permissions: ["CYS_RISK_INLANDFLOOD_BRA_EFAS_C_RP0100"],
    permissions: [PermissionIdEnum.InlandFloodBra100y],
    ...getInlandFloodHazardConfigByYear(100, "BRA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodBra200y,
    // permissions: ["CYS_RISK_INLANDFLOOD_BRA_EFAS_C_RP0200"],
    permissions: [PermissionIdEnum.InlandFloodBra200y],
    ...getInlandFloodHazardConfigByYear(200, "BRA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.InlandFloodBra500y,
    // permissions: ["CYS_RISK_INLANDFLOOD_BRA_EFAS_C_RP0500"],
    permissions: [PermissionIdEnum.InlandFloodBra500y],
    ...getInlandFloodHazardConfigByYear(500, "BRA"),
    pages: [PolicyDetails, LocationRiskAssessment],
  },

  // Global

  {
    cysId: HazardIdEnum.InlandFloodGlobal10y,
    // permissions: ["CYS_RISK_INLANDFLOOD_WRL_GLOFAS_C_RP0010"],
    permissions: [PermissionIdEnum.InlandFloodGlobal10y],
    ...getInlandFloodHazardGlobalConfigByYear(10),
  },
  {
    cysId: HazardIdEnum.InlandFloodGlobal20y,
    // permissions: ["CYS_RISK_INLANDFLOOD_WRL_GLOFAS_C_RP0020"],
    permissions: [PermissionIdEnum.InlandFloodGlobal20y],
    ...getInlandFloodHazardGlobalConfigByYear(20),
  },
  {
    cysId: HazardIdEnum.InlandFloodGlobal50y,
    // permissions: ["CYS_RISK_INLANDFLOOD_WRL_GLOFAS_C_RP0050"],
    permissions: [PermissionIdEnum.InlandFloodGlobal50y],
    ...getInlandFloodHazardGlobalConfigByYear(50),
  },
  {
    cysId: HazardIdEnum.InlandFloodGlobal100y,
    // permissions: ["CYS_RISK_INLANDFLOOD_WRL_GLOFAS_C_RP0100"],
    permissions: [PermissionIdEnum.InlandFloodGlobal100y],
    ...getInlandFloodHazardGlobalConfigByYear(100),
  },
  {
    cysId: HazardIdEnum.InlandFloodGlobal500y,
    // permissions: ["CYS_RISK_INLANDFLOOD_WRL_GLOFAS_C_RP0500"],
    permissions: [PermissionIdEnum.InlandFloodGlobal500y],
    ...getInlandFloodHazardGlobalConfigByYear(500),
  },

  //coastal
  {
    cysId: HazardIdEnum.CoastalFlood10y,
    // permissions: ["CYS_RISK_COASTALFLOOD_EUR_EURAIN2150_RCP45_RP0010"],
    permissions: [PermissionIdEnum.CoastalFlood10y],
    ...getCoastalFloodHazardConfigByYear(10),
  },
  {
    cysId: HazardIdEnum.CoastalFlood30y,
    // permissions: ["CYS_RISK_COASTALFLOOD_EUR_EURAIN2150_RCP45_RP0030"],
    permissions: [PermissionIdEnum.CoastalFlood30y],
    ...getCoastalFloodHazardConfigByYear(30),
  },
  {
    cysId: HazardIdEnum.CoastalFlood100y,
    // permissions: ["CYS_RISK_COASTALFLOOD_EUR_EURAIN2150_RCP45_RP0100"],
    permissions: [PermissionIdEnum.CoastalFlood100y],
    ...getCoastalFloodHazardConfigByYear(100),
  },
  {
    cysId: HazardIdEnum.CoastalFlood300y,
    // permissions: ["CYS_RISK_COASTALFLOOD_EUR_EURAIN2150_RCP45_RP0300"],
    permissions: [PermissionIdEnum.CoastalFlood300y],
    ...getCoastalFloodHazardConfigByYear(300),
  },
  {
    cysId: HazardIdEnum.CoastalFlood1000y,
    // permissions: ["CYS_RISK_COASTALFLOOD_EUR_EURAIN2150_RCP45_RP1000"],
    permissions: [PermissionIdEnum.CoastalFlood1000y],
    ...getCoastalFloodHazardConfigByYear(1000),
  },
  {
    cysId: HazardIdEnum.CoastalFloodEurProt,
    name: "Coastal Flood Protection",
    // permissions: ["CYS_RISK_COASTALFLOOD_EUR_EURAIN2150_RCP45WP_RP1000"],
    permissions: [PermissionIdEnum.CoastalFloodEurProt],
    colorRange: ["#f9f9f9", "#2b83ba", "#abdda4", "#ffffbf", "#fdae61", "#d7191c"],
    description: i18n.t("riskExposureMapExplained.coastalFloodProt"),
    label: `${i18n.t("riskAssessment.coastalFlood")} (EUR) - Prot`,
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.CoastalFloodEurNoProt,
    name: "Coastal Flood Protection",
    // permissions: ["CYS_RISK_COASTALFLOOD_EUR_EURAIN2150_RCP45NP_RP1000"],
    permissions: [PermissionIdEnum.CoastalFloodEurNoProt],
    colorRange: ["#f9f9f9", "#2b83ba", "#abdda4", "#ffffbf", "#fdae61", "#d7191c"],
    description: i18n.t("riskExposureMapExplained.coastalFloodNoProt"),
    label: `${i18n.t("riskAssessment.coastalFlood")} (EUR) - No Prot`,
    pages: [PolicyDetails, LocationRiskAssessment],
  },

  //flash
  {
    cysId: HazardIdEnum.FlashFloodIta3h,
    // permissions: ["CYS_RISK_FLASHFLOOD_EUR_EURAIN3H_C_RP0010"],
    permissions: [PermissionIdEnum.FlashFloodIta3h],
    ...getFlashFloodHazardConfigByYear(3),
  },
  {
    cysId: HazardIdEnum.FlashFloodIta24h,
    // permissions: ["CYS_RISK_FLASHFLOOD_ITA_EURAIN_24H_C_RP0010"],
    permissions: [PermissionIdEnum.FlashFloodIta24h],
    ...getFlashFloodHazardConfigByYear(24),
  },
  {
    cysId: HazardIdEnum.FlashFloodIta48h,
    // permissions: ["CYS_RISK_FLASHFLOOD_ITA_EURAIN_48H_C_RP0010"],
    permissions: [PermissionIdEnum.FlashFloodIta48h],
    ...getFlashFloodHazardConfigByYear(48),
  },
  {
    cysId: HazardIdEnum.FlashFloodIta72h,
    // permissions: ["CYS_RISK_FLASHFLOOD_ITA_EURAIN_72H_C_RP0010"],
    permissions: [PermissionIdEnum.FlashFloodIta72h],
    ...getFlashFloodHazardConfigByYear(72),
  },
];
